import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import MedioMayoristaFiberCard from "../sections/Productos/MedioMayoristaFiberCard";
import MedioMayoristaResinCard from "../sections/Productos/MedioMayoristaResinCard";
import MedioMayoristaFlakesCard from "../sections/Productos/MedioMayoristaFlakesCard";
import MedioMayoristaKitFiberResinCard from "../sections/Productos/MedioMayoristaKitFiberResinCard";
import { useMedioMayoristaCart } from "../context/MedioMayoristaCartContext";
import { iconoMayorista, iconoMedida } from "../assets/landing";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://localhost:89").replace(/\/$/, "");

const parseJsonResponse = async (response, message) => {
  const contentType = response.headers.get("content-type") || "";
  const text = await response.text();
  if (!response.ok || !contentType.includes("application/json")) {
    throw new Error(message);
  }
  return JSON.parse(text);
};

const categoryAnchorBySlug = {
  FibraDeCarbono: "fibra",
  ResinaCatalizador: "resinas",
  Flakes: "flakes",
};

const BenefitIcon = ({ children }) => (
  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-[#67bff5]/60 text-[11px] text-[#5aa8dd] md:h-7 md:w-7 md:text-xs">
    {children}
  </span>
);

const categoryContent = {
  fibra: {
    title: "Fibra de carbono",
    description: "Compra por metro o kilo con entrega rastreada y pedido mínimo flexible.",
  },
  resinas: {
    title: "Resinas y catalizadores",
    description: "Explora resinas, catalizadores y vuelve al inicio cuando quieras seguir con fibra.",
  },
  flakes: {
    title: "Flakes",
    description: "Consulta acabados metálicos y camaleón en una sección dedicada, separada del inicio principal.",
  },
};

const landingPurchaseOptions = [
  {
    title: "Mayorista",
    subtitle: "Para tu negocio.",
    description: "10 m² o más.",
    image: iconoMayorista,
    imageAlt: "Catálogo mayorista",
    to: "/mayorista",
    buttonLabel: "Reduce costos",
    cardClassName: "border border-[#7b328f] bg-[#6a2877]",
    titleClassName: "text-[#e6b7f4]",
    descriptionClassName: "text-[#f6d1ff]",
    buttonClassName: "bg-[#d8a4e9]",
    imageClassName: "w-[88px] md:w-[180px] lg:w-[205px]",
  },
  {
    title: "Por medida",
    subtitle: "Para tu pieza o proyecto.",
    description: "Desde 30 x 20 cm.",
    image: iconoMedida,
    imageAlt: "Kit para proyectos",
    to: "/ecommerce",
    buttonLabel: "Arma tu kit",
    cardClassName: "border border-primary-green bg-[#071a22]",
    titleClassName: "text-primary-green",
    descriptionClassName: "text-white/85",
    buttonClassName: "bg-primary-green",
    imageClassName: "w-[92px] md:w-[190px] lg:w-[220px]",
  },
];

const Inicio = () => {
  const { opcion } = useParams();
  const location = useLocation();
  const { openCart, items, minimumOrderUnits } = useMedioMayoristaCart();

  const [fiberProducts, setFiberProducts] = useState([]);
  const [resinProducts, setResinProducts] = useState([]);
  const [flakesProducts, setFlakesProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fibraRef = useRef(null);
  const resinaRef = useRef(null);
  const flakesRef = useRef(null);

  const selectedAnchor = categoryAnchorBySlug[opcion] || location.hash.replace("#", "") || null;
  const activeCategory = selectedAnchor || "fibra";
  const isLandingHome = location.pathname === "/" && !selectedAnchor;
  const isDedicatedCategoryView = !isLandingHome;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      fetch(`${API_BASE_URL}/api/ecommerce/medio-mayorista/fibra/products`).then((response) =>
        parseJsonResponse(response, "No se pudieron cargar los productos de fibra.")
      ),
      fetch(`${API_BASE_URL}/api/ecommerce/medio-mayorista/resina/products`).then((response) =>
        parseJsonResponse(response, "No se pudieron cargar los productos de resina.")
      ),
      fetch(`${API_BASE_URL}/api/ecommerce/medio-mayorista/flakes/products`).then((response) =>
        parseJsonResponse(response, "No se pudieron cargar los productos de flakes.")
      ),
    ])
      .then(([fibers, resins, flakes]) => {
        if (!active) return;
        setFiberProducts(Array.isArray(fibers) ? fibers : []);
        setResinProducts(Array.isArray(resins) ? resins : []);
        setFlakesProducts(Array.isArray(flakes) ? flakes : []);
      })
      .catch((fetchError) => {
        if (!active) return;
        setError(fetchError.message || "No se pudieron cargar los productos.");
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedAnchor || loading) return;

    const anchorMap = {
      fibra: fibraRef,
      resinas: resinaRef,
      flakes: flakesRef,
    };

    const selectedRef = anchorMap[selectedAnchor];
    if (!selectedRef?.current) return;

    selectedRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selectedAnchor, loading, location.pathname]);

  const sectionCounts = useMemo(
    () => ({
      fibra: fiberProducts.length + 1,
      resinas: resinProducts.length,
      flakes: flakesProducts.length,
    }),
    [fiberProducts.length, flakesProducts.length, resinProducts.length]
  );

  const sectionLabel = useMemo(() => {
    if (minimumOrderUnits === 1) return "Min. 1 unidad";
    return `Min. ${minimumOrderUnits} unidades`;
  }, [minimumOrderUnits]);

  const renderProductsSection = (category) => {
    const sectionMap = {
      fibra: {
        ref: fibraRef,
        id: "fibra",
        title: "Fibra de carbono",
        count: sectionCounts.fibra,
        products: fiberProducts,
        renderGrid: () => (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
            <MedioMayoristaKitFiberResinCard />
            {fiberProducts.map((product) => (
              <MedioMayoristaFiberCard key={product.id} product={product} />
            ))}
          </div>
        ),
      },
      resinas: {
        ref: resinaRef,
        id: "resinas",
        title: "Resinas y catalizadores",
        count: sectionCounts.resinas,
        products: resinProducts,
        renderGrid: () => (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
            {resinProducts.map((product) => (
              <MedioMayoristaResinCard key={product.id} product={product} />
            ))}
          </div>
        ),
      },
      flakes: {
        ref: flakesRef,
        id: "flakes",
        title: "Flakes",
        count: sectionCounts.flakes,
        products: flakesProducts,
        renderGrid: () => (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
            {flakesProducts.map((product) => (
              <MedioMayoristaFlakesCard key={product.id} product={product} />
            ))}
          </div>
        ),
      },
    };

    const section = sectionMap[category];
    if (!section) return null;

    return (
      <section ref={section.ref} id={section.id} className="rounded-2xl bg-[#f4f4f4] p-3 md:p-4">
        <header className="mb-3 flex items-end justify-between md:mb-5">
          <h2 className="border-l-4 border-[#44b5f0] pl-2 text-2xl font-semibold text-[#1c1c1c] md:pl-4 md:text-4xl">
            {section.title}
          </h2>
          <span className="text-[11px] text-black/70 md:text-lg">{section.count} disponibles</span>
        </header>

        {loading ? (
          <div className="rounded-xl border border-[#d0d0d0] bg-white p-5 text-center text-black/65 md:text-lg">
            Cargando productos...
          </div>
        ) : (
          section.renderGrid()
        )}
      </section>
    );
  };

  return (
    <main className="bg-[#efefef]">
      {isLandingHome && (
      <section className="bg-[radial-gradient(circle_at_top,#0e3036_0%,#071820_48%,#041117_100%)] px-4 pb-2 pt-3 text-white md:px-8 md:pb-2 md:pt-3">
        <div className="mx-auto max-w-[1080px]">
          <p className="text-left text-[10px] uppercase tracking-wide text-white/75 md:text-sm md:tracking-normal">
            Envíos a todo México · Facturamos
          </p>
          <h1 className="mt-1 text-left text-[12px] font-black uppercase leading-none tracking-[-0.045em] md:text-[32px] md:leading-tight md:tracking-normal">
            Fibra de carbono como tú la necesites
          </h1>
          <p className="mt-1 text-left text-[11px] text-white/85 md:text-lg">
            Compra al mayoreo, por metros o a medida.
          </p>

          <div className="mt-3 grid grid-cols-2 gap-2 md:mt-3 md:gap-3">
            {landingPurchaseOptions.map((option) => (
              <article
                key={option.title}
                className={`flex h-full flex-col rounded-2xl p-2 md:min-h-[240px] md:rounded-[24px] md:p-3 ${option.cardClassName}`}
              >
                <div className="md:grid md:flex-1 md:grid-cols-[minmax(0,1fr)_auto] md:items-start md:gap-4">
                  <div>
                    <h2 className={`text-left text-xl font-black uppercase md:text-[30px] ${option.titleClassName}`}>
                      {option.title}
                    </h2>
                    <p className="mt-1 text-left text-sm font-bold leading-tight md:mt-1.5 md:text-[17px]">
                      {option.subtitle}
                    </p>
                    <p className={`mt-1 text-left text-[10px] md:text-[14px] ${option.descriptionClassName}`}>
                      {option.description}
                    </p>
                  </div>

                  <div className="mt-1.5 flex min-h-[70px] items-end justify-center md:mt-0 md:min-h-[126px] md:justify-end md:self-end">
                    <img
                      src={option.image}
                      alt={option.imageAlt}
                      className={`h-auto max-h-[70px] object-contain md:max-h-[126px] md:translate-x-2 lg:translate-x-3 ${option.imageClassName}`}
                    />
                  </div>
                </div>

                <div className="mt-auto pt-1.5 md:pt-2">
                  <Link
                    to={option.to}
                    className={`inline-flex w-full items-center justify-center rounded-lg px-2.5 py-1.5 text-center text-xs font-bold text-black md:rounded-xl md:px-5 md:py-1.5 md:text-base ${option.buttonClassName}`}
                  >
                    {option.buttonLabel} <span className="ml-2">→</span>
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <p className="mt-2 text-center text-[9px] uppercase tracking-[0.15em] text-white/55 md:mt-2 md:text-[11px] md:tracking-[0.2em]">
            Desliza para ver productos
          </p>
        </div>
      </section>
      )}

      {isLandingHome && (
      <section className="border-y border-[#d5e5f2] bg-[#d9edf9] px-4 py-2.5 md:px-8 md:py-3">
        <div className="mx-auto max-w-[1240px]">
          <p className="text-center text-sm font-bold text-[#0f4fa3] md:text-lg">
            Compra por metro o kilo · {sectionLabel}
          </p>
          <div className="mt-2 grid grid-cols-3 gap-2 border-t border-[#b2d5ee] pt-2 text-[10px] text-[#3e6b87] md:mt-2 md:gap-4 md:pt-2 md:text-sm">
            <div className="flex items-center justify-center gap-2">
              <BenefitIcon>👥</BenefitIcon>
              <span>320+ clientes</span>
            </div>
            <div className="flex items-center justify-center gap-2 border-x border-[#b2d5ee]">
              <BenefitIcon>🚚</BenefitIcon>
              <span>Envío rastreado</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <BenefitIcon>🧾</BenefitIcon>
              <span>Factura</span>
            </div>
          </div>
        </div>
      </section>
      )}

      <section className="px-4 py-5 md:px-8 md:py-8">
        <div className={`mx-auto space-y-7 md:space-y-10 ${isDedicatedCategoryView ? "max-w-[1100px]" : "max-w-[1240px]"}`}>
          {error && (
            <div className="rounded-xl border border-[#e18a8a] bg-[#ffecec] p-4 text-center text-[#7f1f1f] md:text-xl">
              {error}
            </div>
          )}

          {isDedicatedCategoryView && !error && (
            <section className="rounded-2xl border border-[#cfe6f4] bg-[#f5fbff] px-4 py-4 md:px-6 md:py-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="text-left">
                  <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-primary-green md:text-sm">
                    Ecommerce Med
                  </p>
                  <h1 className="text-2xl font-black text-[#1b1b1b] md:text-4xl">
                    {categoryContent[activeCategory]?.title || "Productos"}
                  </h1>
                  <p className="mt-1 text-sm text-[#4a6576] md:text-base">
                    {categoryContent[activeCategory]?.description}
                  </p>
                </div>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center rounded-full border border-primary-green bg-white px-4 py-2 text-sm font-bold text-primary-green transition hover:bg-[#f2fbf2] md:text-base"
                >
                  Volver a fibra de carbono
                </Link>
              </div>
            </section>
          )}

          {!error && (
            <>
              {isLandingHome && renderProductsSection("fibra")}
              {isDedicatedCategoryView && renderProductsSection(activeCategory)}
            </>
          )}
        </div>
      </section>

      {isLandingHome && (
      <section className="px-4 pb-8 md:px-8 md:pb-12">
        <div className="mx-auto max-w-[920px] rounded-xl bg-[#d9edf9] px-4 py-4 md:px-6 md:py-5">
          <p className="text-center text-base font-bold text-[#0f4fa3] md:text-2xl">
            Compra por metro o kilo · {sectionLabel}
          </p>
          <p className="mt-1 text-center text-xs text-[#2f4d61] md:text-base">
            ¿Necesitas fibra para tu pieza o presentaciones de mayor volumen? Vuelve arriba y elige la
            opción.
          </p>
        </div>

        <div className="mx-auto mt-4 max-w-[760px] text-center md:mt-8">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-primary-green md:text-sm">
            No te vayas sin ver
          </p>
          <h3 className="text-xl text-[#1b1b1b] md:text-3xl">También tenemos</h3>

          <div className="mt-3 grid grid-cols-2 gap-3 md:mt-5 md:gap-4">
            <Link
              to="/productos/ResinaCatalizador"
              className="overflow-hidden rounded-xl border border-primary-green bg-white text-left"
            >
              <div className="h-20 bg-[#f9f9f9] p-3 md:h-32">
                <img src="/ecommerce/Resinas.png" alt="Resinas" className="h-full w-full object-contain" />
              </div>
              <div className="bg-[#ecf2e9] px-3 py-2 md:px-4 md:py-3">
                <p className="text-lg text-[#1d1d1d] md:text-2xl">Resinas</p>
                <p className="text-[10px] text-[#808080] md:text-sm">Epóxica y gel coat</p>
                <p className="mt-1 text-xs font-bold text-primary-green md:text-base">Ver resinas →</p>
              </div>
            </Link>

            <Link
              to="/productos/Flakes"
              className="overflow-hidden rounded-xl border border-primary-green bg-white text-left"
            >
              <div className="h-20 bg-[#f9f9f9] p-3 md:h-32">
                <img src="/ecommerce/Flakes.png" alt="Flakes" className="h-full w-full object-contain" />
              </div>
              <div className="bg-[#ecf2e9] px-3 py-2 md:px-4 md:py-3">
                <p className="text-lg text-[#1d1d1d] md:text-2xl">Flakes</p>
                <p className="text-[10px] text-[#808080] md:text-sm">Metálicos, tornasol, camaleón</p>
                <p className="mt-1 text-xs font-bold text-primary-green md:text-base">Ver flakes →</p>
              </div>
            </Link>
          </div>
        </div>
      </section>
      )}

      <button
        type="button"
        onClick={openCart}
        className="fixed bottom-5 right-4 z-40 rounded-full bg-primary-green p-3 text-white shadow-[0_10px_24px_rgba(0,0,0,0.35)] md:bottom-8 md:right-8 md:p-4"
        aria-label="Abrir carrito"
        data-floating-cart-anchor="bottom-right"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-6 w-6 md:h-8 md:w-8"
        >
          <circle cx="9" cy="20" r="1.6" />
          <circle cx="18" cy="20" r="1.6" />
          <path d="M2 2h2l2.4 11.5a2 2 0 0 0 2 1.6h8.8a2 2 0 0 0 2-1.6L21 6H7" />
        </svg>
        {!!items.length && (
          <span className="absolute -right-1 -top-1 flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#ff3131] px-1 text-xs font-bold">
            {items.length}
          </span>
        )}
      </button>
    </main>
  );
};

export default Inicio;
